/**
 * Scroll modes (FR-35). Ticket 09 built the two non-speech modes: `manual`
 * (position driven only by the nudge hotkeys) and `constant` (advanced at a
 * configured words-per-minute). Ticket 14 adds `speech` at the head of the cycle.
 * Keeping the ordered list and the cycle helper in one pure module means both the
 * main process (which owns the live mode) and the renderer (which labels it) agree
 * on the vocabulary.
 *
 * Scroll-and-bar ticket 03 adds {@link scrollbarLive}, which is the same
 * vocabulary answering a different question: not how the position is driven, but
 * whether anything is driving it at all.
 */

import type { RecordingPhase } from "./recording.js";
import { isRecordingActive } from "./recording.js";

/** How the prompter's position is driven. */
export type ScrollMode = "speech" | "manual" | "constant";

/**
 * The cycle order the mode hotkey (`Ctrl+Alt+M`) steps through, wrapping at the
 * end.
 *
 * Speech leads, and that is a product decision rather than an alphabetical
 * accident: following the speaker is the only reason to use cuekards over any other
 * teleprompter (PRD §1), so it is what you get without asking. The two behind it
 * are the fallbacks in descending order of usefulness — manual never moves the
 * script on its own, constant is the conventional prompter FR-35 keeps as the
 * floor (G5).
 */
export const SCROLL_MODES: readonly ScrollMode[] = [
  "speech",
  "manual",
  "constant",
];

/** The mode a freshly launched overlay is in — the first of the cycle. */
export const DEFAULT_SCROLL_MODE: ScrollMode = SCROLL_MODES[0]!;

/** The next mode in the cycle, wrapping back to the first after the last. */
export function nextScrollMode(mode: ScrollMode): ScrollMode {
  const i = SCROLL_MODES.indexOf(mode);
  return SCROLL_MODES[(i + 1) % SCROLL_MODES.length]!;
}

/**
 * A mode that arrived over a channel, read the one way (flow ticket 06).
 *
 * One function rather than the same guard written at each host's end of
 * `setScrollMode`, so the two cannot come to different conclusions about what a
 * malformed one means. That is `asStepDirection`'s argument and it is the same
 * one here.
 *
 * **Anything unrecognised is `constant`, not {@link DEFAULT_SCROLL_MODE}.** The
 * default is what a freshly launched overlay *chooses*, and speech leads that
 * cycle because following the speaker is the reason this product exists. Where a
 * message nobody can read lands is a different question, and the answer to it
 * must not be a mode that needs a download nobody has been offered. `constant` is
 * the conventional prompter FR-35 keeps as the floor (G5), which is the direction
 * doubt falls in here — the same direction, and for the same kind of reason, as
 * `getVoiceCommandsEnabled`'s.
 */
export function asScrollMode(value: unknown): ScrollMode {
  return SCROLL_MODES.includes(value as ScrollMode)
    ? (value as ScrollMode)
    : "constant";
}

/** Short uppercase labels shown in the always-visible overlay status indicator
 * (FR-28). Constant appends its live WPM at the render site. */
export const SCROLL_MODE_LABEL: Record<ScrollMode, string> = {
  speech: "SPEECH",
  manual: "MANUAL",
  constant: "CONSTANT",
};

/** Everything that decides whether the reader may scroll the script by hand. */
export interface ScrollbarConditions {
  mode: ScrollMode;
  phase: RecordingPhase;
  /** Whether a rehearsal has been asked for (FR-37). */
  rehearsing: boolean;
  /** The anchored word's index in the spoken-word stream, `-1` before the first. */
  position: number;
  /** How many spoken words the loaded script has. */
  wordCount: number;
}

/**
 * Whether the prompter's scrollbar is live (scroll-and-bar ticket 03).
 *
 * **The rule is stated over drivers, not over the transport.** Three things can
 * move the anchor — the speech follower, the constant-speed timer, and the reader's
 * own hands — and only the first is really about recording. A rule phrased as
 * "locked while rehearsing or recording" would leave the constant crawl running
 * under a live scrollbar at idle, which is the one arrangement that cannot work:
 * you would scroll, and the text would walk out from under you a word at a time.
 *
 * So: **live exactly when nothing else is driving the anchor.** In MANUAL mode
 * that is always, including mid-take, because in manual mode nothing but the
 * reader ever moves the position. In CONSTANT it is once the crawl has reached the
 * end, or while a take is paused. In SPEECH it is at idle and while paused.
 *
 * That last clause is what ticket 02 buys: a pause genuinely stops every driver,
 * so the anchor the reader scrolls away from is still there when they resume.
 *
 * Locked is not the same as frozen. Scrolling is the *view*, and the view keeps
 * following the anchor while locked — this decides who is allowed to move it, not
 * whether it moves.
 */
export function scrollbarLive({
  mode,
  phase,
  rehearsing,
  position,
  wordCount,
}: ScrollbarConditions): boolean {
  // A paused take has stopped every driver, whatever the mode (ticket 02), so it
  // short-circuits before the modes are considered at all.
  if (phase === "paused") return true;
  if (mode === "manual") return true;
  if (mode === "constant") {
    // The crawl stops of its own accord at the last word and does not re-arm, so
    // from there on nothing is driving and the script is free to be read back.
    return position >= wordCount - 1;
  }
  return !rehearsing && !isRecordingActive(phase);
}
